import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import { usePortalIdentity } from "../../auth/usePortalIdentity";
import { useClientSiteFactsSites, type ClientFactsSite } from "../../data/clientFactsQueries";
import { BoxState } from "../../ui/BoxState";
import { ClientHead } from "../../ui/ClientHead";
import { Hint } from "../../ui/Hint";
import { Panel } from "../../ui/Panel";
import { formatWhen, inSentence } from "../clientOps/format";
import { FactsForSite } from "./FactsForSite";
import { PAGE_HINT, PAGE_TITLE } from "./clientFactsWords";
import "../clientOps/clientOps.css";
import "../clientSettings/settings.css";
import { useClientPath } from "../../data/readSource";

/**
 * Webbplatsens fakta, `/site-facts` and `/site-facts/:serviceId`, since build round
 * 19 (2026-10-08), the third CMS round of decision 0503: "Clients edit facts in the Portal, and the
 * Portal can only start a build, never write code."
 *
 * The place exists for a client only when one of its websites has facts to show: a stored schema and
 * at least one version Petrovic published (`client_site_facts_sites`). The shell offers it on that
 * answer alone. Somebody who reaches the address without a site, by an old link or by typing it, is
 * told calmly that there is nothing here yet, because nothing is wrong.
 *
 * One website opens its facts at `/site-facts` directly; several are listed there first, and
 * each opens at its own address, so a link can name one. Decided with the round's contract on
 * 2026-10-08. The heading is always the place's name, so it reads the same in every state.
 *
 * The head and its Hint are drawn in every state, as on Konton: the line that says what the page is
 * for has to survive into the states where the page has nothing to show.
 */

const BASE = "/site-facts";

function SitesList({ sites }: { sites: ClientFactsSite[] }) {
  const clientPath = useClientPath();
  return (
    <Panel title="Era webbplatser" titleId="client-facts-sites-title" flush testId="client-facts-sites">
      <ul className="k-rows" aria-labelledby="client-facts-sites-title">
        {sites.map((site) => (
          <li key={site.service_id}>
            <Link className="k-row" to={clientPath(`${BASE}/${site.service_id}`)} data-testid="client-facts-site-link">
              <span className="k-row-main primary-cell">
                <strong>{site.name}</strong>
                <span>
                  Version {site.latest_version}, publicerad {inSentence(formatWhen(site.published_at) ?? "")}
                </span>
              </span>
              <CaretRight size={16} aria-hidden="true" className="k-row-go" />
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function ClientFactsRoute() {
  const clientPath = useClientPath();
  const { serviceId } = useParams<{ serviceId?: string }>();
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const sites = useClientSiteFactsSites(tenantId);
  const several = (sites.data?.length ?? 0) > 1;

  const page = (content: ReactNode) => (
    <div className="k-page" data-testid="client-facts">
      <ClientHead
        title={PAGE_TITLE}
        back={serviceId && several ? { to: clientPath(BASE), label: "Alla webbplatser" } : undefined}
        after={<Hint>{PAGE_HINT}</Hint>}
      />
      {content}
    </div>
  );

  const box = (state: ReactNode) => <section className="panel">{state}</section>;

  if (identity.isLoading || sites.isLoading) {
    return page(box(<BoxState kind="loading" title="Hämtar uppgifterna" />));
  }

  // Before the tenant check, as on Konton: a failed identity read answers with no tenant, and that
  // is not "this account belongs to no customer".
  if (identity.isError) {
    return page(
      box(
        <BoxState
          kind="error"
          title="Uppgifterna gick inte att hämta"
          body="Vi kunde inte läsa vem du är. Försök igen, och hör av dig i chatten om det upprepas."
          onRetry={() => void identity.refetch()}
        />
      )
    );
  }

  if (!tenantId) {
    return page(
      box(<BoxState kind="denied" title="Den här vyn är inte din" body="Kontot hör inte till någon kund i portalen." />)
    );
  }

  // Only when there is no list at all. A list read before and failing to be read again keeps the page,
  // because what is on it may be a half-typed form, and the website's own read says when it is stale.
  if (!sites.data) {
    return page(
      box(
        <BoxState
          kind="error"
          title="Uppgifterna gick inte att hämta"
          body={`${sites.error?.message ?? "Läsningen gick inte fram."} Det betyder inte att er webbplats saknar uppgifter.`}
          onRetry={() => void sites.refetch()}
        />
      )
    );
  }

  const list = sites.data;

  if (list.length === 0) {
    return page(
      box(
        <BoxState
          kind="empty"
          title="Här finns inget att ändra än"
          body="När uppgifterna på er webbplats går att ändra här, öppettider och priser till exempel, finns de på den här sidan."
        />
      )
    );
  }

  if (serviceId) {
    const site = list.find((candidate) => candidate.service_id === serviceId);
    if (!site) {
      return page(
        box(
          <BoxState
            kind="empty"
            title="Webbplatsen finns inte här"
            body="Adressen leder inte till någon av era webbplatser. Gå till Webbplatsens fakta i menyn."
          />
        )
      );
    }
    return page(<FactsForSite key={site.service_id} tenantId={tenantId} site={site} />);
  }

  if (list.length === 1) {
    return page(<FactsForSite key={list[0].service_id} tenantId={tenantId} site={list[0]} />);
  }

  return page(<SitesList sites={list} />);
}
